<template>
  <vs-card v-if="resource" :to="linkTo">
    <div>
      <v-row dense>
        <v-col>
          <vs-text type="overline">{{ division.name }}: {{ company.name }}</vs-text>
          <vs-heading>{{ resource.name }}</vs-heading>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col>
          <vs-text type="caption">
            {{ resourceType.name }}
          </vs-text>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col class="shrink pr-2">
          <v-icon large color="grey darken-2">{{ status.icon }}</v-icon>
        </v-col>

        <v-col>
          <vs-text type="overline">Status</vs-text>
          <vs-text>{{ status.description }}</vs-text>
        </v-col>
      </v-row>
    </div>
  </vs-card>
</template>

<script setup lang="ts">
import { computed, toRef } from 'vue'
import VsText from '@/components/vision/VsText.vue'
import VsCard from '@/components/vision/VsCard.vue'
import { useResourceQuery } from '@/api/unit_numbers'
import type { RouterLinkProps } from 'vue-router/types/router'
import { useStore } from 'vuex-composition-helpers'
import Division from '@/models/division'
import ResourceType from '@/models/resource_type'
import Company from '@/models/company'

type RouterLinkTo = RouterLinkProps['to']

const props = defineProps<{
  resourceId: string
  linkTo: RouterLinkTo
}>()

const store = useStore()

const resourceIdRef = toRef(props, 'resourceId')
const { data: resource } = useResourceQuery(resourceIdRef)

const getDivisionById = computed<(id: number | null) => Division>(
  () => store.getters['divisions/getById'],
)
const getResourceTypeById = computed<(id: number | null) => ResourceType>(
  () => store.getters['resourceTypes/getById'],
)

const getCompanyByOrganizationId = computed<(id: string | null) => Company>(
  () => store.getters['companies/getByOrganizationId'],
)

const division = computed(() => getDivisionById.value(resource.value.divisionId))
const resourceType = computed(() => getResourceTypeById.value(resource.value.resourceTypeId))
const company = computed(() => getCompanyByOrganizationId.value(resource.value.rootOrganizationId))

enum Status {
  OFFLINE = 0,
  STANDBY = 1,
  ACTIVE = 2,
}

const status = computed(() => {
  switch (resource.value?.status) {
    case Status.OFFLINE:
      return {
        icon: 'mdi-close-circle',
        description: 'Offline',
      }
    case Status.STANDBY:
      return {
        icon: 'pause_circle_filled',
        description: 'Standby',
      }
    case Status.ACTIVE:
      return {
        icon: 'play_circle_filled',
        description: 'Active',
      }
    default:
      return {
        icon: 'help_outline',
        description: 'Loading',
      }
  }
})
</script>
